import { FormControl, FormControlLabel, FormLabel, makeStyles, Radio, RadioGroup } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import { DateTime } from 'luxon';
import { memo } from 'react';
import { Control, Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UserPermissions, useUserPermissions } from 'src/hooks/useUserPermissions';
import { useAccountDealerId } from 'src/modules/bid-buy-helpers';
import { DATE_TIME_FORMAT } from 'src/modules/date-helpers';

const useStyles = makeStyles(() => ({
  formGroup: {
    flexDirection: 'row',
    '& .Mui-checked': {
      color: grey[800],
    },
    '& .Mui-checked  + span': {
      color: grey[800],
    },
  },
  datePicker: {
    width: '50%',
  },
}));

type BuyingTimeRadiosProps = {
  name: string;
  control: Control;
  activateForm: boolean;
  isFirstCall?: boolean;
  endDate?: string;
};

const BiddingTimeRadios = ({ name, control, activateForm, endDate, isFirstCall = false }: BuyingTimeRadiosProps) => {
  const classes = useStyles();
  const { setValue, watch } = useFormContext();
  const { t } = useTranslation();
  const currentDealer = useAccountDealerId();
  const allowedToSetBuyingTime = useUserPermissions(UserPermissions.FIXED_BUY_TIME);
  const setTimeActive = watch('overrideFirstCallCascade') === 'biddingTime';

  /// reset the value overridingFirstCallCascadeDealer on every change to make sure everything gets rendered correctly
  const resetFirstCallDealer = () => {
    if (typeof currentDealer === 'string') {
      setValue('overridingFirstCallCascadeDealer', currentDealer);
    } else {
      setValue('overridingFirstCallCascadeDealer', '');
    }
  };

  return (
    <FormControl component="fieldset" margin="dense" fullWidth>
      <FormLabel component="legend">{t('bids.buyAtAuctionEnd')}</FormLabel>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value } }) => (
          <RadioGroup
            className={classes.formGroup}
            value={value}
            onChange={(e) => {
              onChange(e);
              resetFirstCallDealer();
            }}
          >
            {[
              {
                value: 'no',
                label: t('bids.submissionAtAuctionEnd_short'),
              },
              ...(isFirstCall ? [{ value: 'yes', label: t('bids.instantly') }] : []),
              ...(allowedToSetBuyingTime ? [{ value: 'biddingTime', label: t('bids.fixedPurchaseTime') }] : []),
            ].map(({ value: itemValue, label }) => {
              const labelWithHint = `${label} `;
              return (
                <FormControlLabel
                  key={label}
                  value={itemValue}
                  disabled={!activateForm}
                  control={<Radio />}
                  label={labelWithHint}
                />
              );
            })}
          </RadioGroup>
        )}
      />
      {setTimeActive && (
        <Controller
          control={control}
          name="bid.biddingTime"
          defaultValue={DateTime.now().toISO()}
          rules={{ required: true }}
          render={({ field: { onChange, ref, value, ...field } }) => (
            <KeyboardDateTimePicker
              {...field}
              ampm={false}
              inputRef={ref}
              className={classes.datePicker}
              format={DATE_TIME_FORMAT}
              invalidDateMessage={t('carsTable.formError.dateTimeError')}
              maxDateMessage={t('bids.hintEndTimeAuction')}
              inputVariant="outlined"
              margin="dense"
              onChange={(v) => {
                onChange(v?.toISO());
              }}
              label={t('bids.purchaseTime')}
              openTo="date"
              placeholder="TT.MM.JJ SS:MM"
              maxDate={endDate}
              value={value !== '60' ? value : DateTime.now().toISO()}
              minDate={DateTime.now().toISO()}
              minDateMessage={t('bids.hintPast')}
            />
          )}
        />
      )}
    </FormControl>
  );
};

export default memo(BiddingTimeRadios);
